import { useMemo } from "react";
import { Text } from "@chakra-ui/react";
import { FiRotateCcw } from "react-icons/fi";

import { Button } from "newui/components/Button";
import {
	CompactSelect,
	InputWithLabel,
	TextAreaWithLabel,
} from "newui/components/ui/InputWithLabel";
import { FormLabel } from "newui/components/ui/FormLabel";

import { SpreadsheetGrid } from "../../SpreadsheetEditor";
import { TemplateRowsBody } from "../components";
import type { QuoteTemplate } from "../../../api/quote-templates-api";
import type { TemplateDraftApi } from "./hooks/useTemplateDraft";
import { BLANK_TEMPLATE_VALUE } from "./types";
import { Help } from "newui/components/ui/Help";
import { buildQuoteSettingsSteps } from "guides/features/QuoteTable/quoteTableSteps";

export interface TemplateEditorProps {
	canUndo: boolean;
	draft: TemplateDraftApi;
	isCreating: boolean;
	isEditing: boolean;
	isSaving: boolean;
	templates: QuoteTemplate[];
	validationError: string | null;
	onCancel: () => void;
	onSave: () => void;
	onStartFromTemplate: (templateId: string | number) => void;
	onUndo: () => void;
}

/**
 * The editor card. Renders the name/description/quote-table-name fields, the
 * "Start from" picker (creation only), the SpreadsheetGrid for columns + rows,
 * the validation banner and the Cancel/Save footer.
 *
 * All state lives in the orchestrator via the `draft` prop (a
 * useTemplateDraft return value); this component is pure presentation plus
 * the simple "select all" UI state which is read directly off the draft.
 */
export const TemplateEditor: React.FC<TemplateEditorProps> = ({
	draft,
	isCreating,
	isEditing,
	isSaving,
	canUndo,
	validationError,
	templates,
	onUndo,
	onCancel,
	onSave,
	onStartFromTemplate,
}) => {
	const {
		draftName,
		setDraftName,
		draftDescription,
		setDraftDescription,
		draftQuoteTableName,
		setDraftQuoteTableName,
		draftRows,
		selectedRowIds,
		handleAddRow,
		handleCellChange,
		handlePasteMatrix,
		handleRemoveRow,
		handleRowChange,
		handleReorderRows,
		handleToggleRowSelected,
		handleToggleSelectAll,
		handleBulkRemoveRows,
		columnManager,
		rowsMissingUnit,
		unitOptions,
	} = draft;

	const {
		addColumn,
		columns,
		duplicateColumn,
		getDisabledReason,
		insertColumn,
		isTypeDisabled,
		moveColumn,
		removeColumn,
		reorderColumns,
		updateColumnLabel,
		updateColumnType,
	} = columnManager;

	// Build the guided-tour steps memoised on the draft's row-mutation
	// handlers — the "Copy from Excel" step needs to drop a demo row into the
	// editor and clean it up after the step.
	const helpSteps = useMemo(
		() =>
			buildQuoteSettingsSteps({
				addDemoRow: (id) => draft.addRowWithId(id),
				removeDemoRow: (id) => draft.handleRemoveRow(id),
			}),
		[draft]
	);

	const startFromOptions = useMemo(
		() => [
			{ label: "Blank template", value: BLANK_TEMPLATE_VALUE },
			...templates.map((t) => ({ label: t.name, value: String(t.id) })),
		],
		[templates]
	);

	const allSelected =
		draftRows.length > 0 && selectedRowIds.size === draftRows.length;
	const someSelected =
		selectedRowIds.size > 0 && selectedRowIds.size < draftRows.length;

	const saveDisabled = isSaving || !draftName.trim() || rowsMissingUnit.size > 0;

	return (
		<div
			className="rounded-xl border border-[#EAECF0] bg-white shadow-sm"
			data-testid="template-editor"
		>
			<div className="flex items-center justify-between border-b border-[#EAECF0] px-4 py-3">
				<Text className="font-roboto text-sm font-semibold text-gray-900">
					{isEditing ? "Edit Template" : "New Template"}
					<Help className="pl-4" guideSteps={helpSteps} />
				</Text>
				<Button
					size="xs"
					btntype="secondary"
					onClick={onUndo}
					disabled={!canUndo}
					aria-label="Undo last change"
					data-testid="undo-template-btn"
				>
					<FiRotateCcw className="mr-1" />
					Undo
				</Button>
			</div>

			<div className="px-4 py-3">
				{/* Phase B — Start from an existing template (creation only) */}
				{isCreating && templates.length > 0 && (
					<div className="mb-4 max-w-sm" data-testid="start-from-template">
						<Text className="mb-1 text-xs font-medium text-gray-700">
							Start from
						</Text>
						<CompactSelect
							options={startFromOptions}
							value={BLANK_TEMPLATE_VALUE}
							onChange={(e) => onStartFromTemplate(e.target.value)}
							aria-label="Start from template"
							data-testid="start-from-template-select"
						/>
					</div>
				)}

				<div className="mb-4">
					<InputWithLabel
						label="Template Name"
						required
						hasError={!draftName.trim()}
						errorText={
							!draftName.trim() ? "Template name is required" : undefined
						}
						value={draftName}
						onChange={(e) => setDraftName(e.target.value)}
						placeholder="e.g. Standard Construction Quote"
						aria-label="Template name"
						data-testid="template-name-input"
					/>
				</div>

				<div data-testid="template-quote-table-defaults">
					<div className="mb-1 mt-1 text-[11px] font-semibold uppercase tracking-wide text-tertiary-500">
						Defaults applied to a Quote Table created from this template
					</div>

					<div className="mb-3">
						<InputWithLabel
							label="Quote Table Name"
							value={draftQuoteTableName}
							onChange={(e) => setDraftQuoteTableName(e.target.value)}
							placeholder="e.g. Preliminaries"
							aria-label="Quote table name"
							data-testid="template-quote-table-name-input"
						/>
					</div>

					<div className="mb-4">
						<TextAreaWithLabel
							label="Quote Table Description"
							value={draftDescription}
							onChange={(e) => setDraftDescription(e.target.value)}
							placeholder="What does this table cover? Suppliers will see this to understand what you're asking for."
							rows={2}
							maxLength={2000}
							aria-label="Quote table description"
							data-testid="template-description-input"
						/>
					</div>
				</div>

				<div className="mb-2" data-testid="template-columns-editor">
					<FormLabel label="Columns & Rows" />
					<div className="mb-2 flex items-center gap-3">
						<label className="flex items-center gap-1 text-xs text-gray-700 cursor-pointer">
							<input
								type="checkbox"
								checked={allSelected}
								ref={(el) => {
									if (el) el.indeterminate = someSelected;
								}}
								onChange={handleToggleSelectAll}
								disabled={draftRows.length === 0}
								aria-label="Select all rows"
								data-testid="template-rows-select-all"
								className="cursor-pointer"
							/>
							Select all
						</label>
						{selectedRowIds.size > 0 && (
							<Button
								size="xs"
								btntype="secondary"
								onClick={handleBulkRemoveRows}
								data-testid="template-rows-bulk-delete-btn"
							>
								Delete {selectedRowIds.size} row
								{selectedRowIds.size === 1 ? "" : "s"}
							</Button>
						)}
					</div>
					<SpreadsheetGrid
						columns={columns}
						getDisabledReason={getDisabledReason}
						isTypeDisabled={isTypeDisabled}
						onAddColumn={addColumn}
						onDuplicateColumn={duplicateColumn}
						onInsertColumn={insertColumn}
						onMoveColumn={moveColumn}
						onRemoveColumn={removeColumn}
						onReorderColumns={reorderColumns}
						onUpdateLabel={updateColumnLabel}
						onUpdateType={updateColumnType}
						renderBody={(cols) => (
							<TemplateRowsBody
								columns={cols}
								rows={draftRows}
								unitOptions={unitOptions}
								rowsWithUnitError={rowsMissingUnit}
								onAddRow={handleAddRow}
								onChangeRow={handleRowChange}
								onChangeCell={handleCellChange}
								onPasteMatrix={handlePasteMatrix}
								onRemoveRow={handleRemoveRow}
								onReorderRows={handleReorderRows}
								selectedRowIds={selectedRowIds}
								onToggleRowSelected={handleToggleRowSelected}
							/>
						)}
					/>
				</div>

				{validationError && (
					<Text
						className="mt-2 text-xs text-red-600"
						data-testid="template-validation-error"
					>
						{validationError}
					</Text>
				)}
			</div>

			<div className="flex items-center justify-end gap-2 border-t border-[#EAECF0] px-4 py-3">
				<Button
					size="sm"
					btntype="secondary"
					onClick={onCancel}
					data-testid="cancel-template-btn"
				>
					Cancel
				</Button>
				<Button
					size="sm"
					btntype="primary"
					onClick={onSave}
					disabled={saveDisabled}
					data-testid="save-template-btn"
				>
					{isSaving
						? "Saving..."
						: isEditing
							? "Update Template"
							: "Create Template"}
				</Button>
			</div>
		</div>
	);
};
